import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DialogLayoutDisplay } from '@costlydeveloper/ngx-awesome-popup';
import { ResourceService } from '@direction/translate';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TypeMessageSignalR } from '../models/socketMessage';
import { Status, StatusCode } from '../models/status';
import { StatusSignalR } from '../models/statusSignalR';
import { HelpersService } from './helpers.service';

@Injectable()
export class TurnstileService {
  isConnection = false;
  eventSubject$ = new Subject<any>();
  closeEventsSubject$ = new Subject<any>();
  eventWatchdog$ = new Subject<any>();
  studentsByDay$ = new Subject<any>();
  connection!: signalR.HubConnection;
  unitCode$ = new BehaviorSubject<any>(undefined);
  programCodes$ = new BehaviorSubject<any>(undefined);
  turnstileId$ = new BehaviorSubject<any>(undefined);
  eventSignalR$ = new BehaviorSubject<any>(StatusSignalR.Unknown);
  constructor(private helpersService: HelpersService, private resource: ResourceService, private http: HttpClient) {}

  initConnection() {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.urlWebSocket, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on('CredentialEntered', (...args: any[]) => {
      this.eventSubject$.next(args);
      this.eventSignalR$.next({ type: TypeMessageSignalR.CredentialEntered, data: args });
    });

    this.connection.on('Watchdog', (...args: any[]) => {
      this.eventWatchdog$.next({ date: new Date(Date.now()).toLocaleString('pt-BR') });
    });

    this.connection.onclose((error?: Error) => {
      if (error) {
        this.eventSubject$.error(error);
      } else {
        this.eventSubject$.complete();
      }
    });

    this.connection
      .start()
      .then(() => {
        this.closeEventsSubject$.next({ sucess: true });
        this.eventSignalR$.next({ status: StatusSignalR.Connected });
      })
      .catch((error) => {
        this.closeEventsSubject$.next({ error: true });
        this.eventSubject$.next({ disconnected: true });
        this.eventSignalR$.next({ status: StatusSignalR.Disconnected });
      });
    return this.eventSubject$;
  }

  sendReleaseEntrance(data: any) {
    this.connection.invoke('ReleaseEntrance', data).then((response) => {
      this.showToast(response.statusCode);
    });
  }

  rebooTurnstilesManager() {
    let data = {
      jwt: this.helpersService.getDsAuthJWTToken().getValue()
    };
    this.connection.invoke('reboot', data).then((response) => {
      this.showToast(
        response.statusCode,
        this.resource.getResource('@direction.toast.reboot.title'),
        this.resource.getResource('@direction.toast.reboot.message')
      );
    });
  }

  updateStudent(student: any) {
    student.jwt = this.helpersService.getDsAuthJWTToken().getValue();
    return this.connection.invoke('UpdateStudent', student);
  }

  addNewStudent(student: any) {
    student.jwt = this.helpersService.getDsAuthJWTToken().getValue();
    return this.connection.invoke('AddNewStudent', student);
  }

  deleteStudent(student: any) {
    student.jwt = this.helpersService.getDsAuthJWTToken().getValue();
    return this.connection.invoke('DeleteStudent', student);
  }

  showToast(statusCode: StatusCode, title?: string, message?: string) {
    switch (statusCode) {
      case StatusCode.Success:
        this.helpersService.showToast(
          DialogLayoutDisplay.SUCCESS,
          title ?? this.resource.getResource('@direction.toast.authorizeTitle'),
          message ?? this.resource.getResource(Status.getMessage(statusCode))
        );
        break;

      default:
        this.helpersService.showToast(
          DialogLayoutDisplay.WARNING,
          title ?? this.resource.getResource('@direction.toast.authorizeTitle'),
          this.resource.getResource(Status.getMessage(statusCode))
        );
        break;
    }
  }

  otherChannel(channelName: string) {
    this.connection.on(channelName, (...args: any[]) => {
      this.eventSubject$.next(args);
    });
  }

  statusServer() {
    this.connection?.onreconnecting((data) => {
      this.closeEventsSubject$.next(data);
    });
    this.connection?.onreconnected((data) => {
      this.closeEventsSubject$.next({ sucess: true });
    });
    return this.closeEventsSubject$;
  }

  playAudioHappyBirthday() {
    let audio = new Audio();
    audio.src = './assets/sound/happy-birthday_edit.wav';
    audio.load();
    audio.play();
  }

  getPrograms(name: string, year?: number): Observable<any> {
    if (typeof year !== 'number') {
      // Pega o ano atual caso não seja informado.
      year = new Date().getFullYear();
    }
    return this.http.get<any>(
      `${environment.api}/programs?$filter=year(startat) eq ${year} and contains(toLower(description), '${name.toLowerCase()}')`
    );
  }

  getProgramsWithUnitCode(name: string, unitCode: string, year?: number): Observable<any> {
    if (typeof year !== 'number') {
      // Pega o ano atual caso não seja informado.
      year = new Date().getFullYear();
    }
    return this.http.get<any>(
      `${
        environment.api
      }/programs?$filter=year(startat) eq ${year} and unitCode eq '${unitCode}' and contains(toLower(description), '${name.toLowerCase()}')`
    );
  }

  getProgramsByProgamCode(programCode: string, year?: number): Observable<any> {
    if (typeof year !== 'number') {
      // Pega o ano atual caso não seja informado.
      year = new Date().getFullYear();
    }
    return this.http.get<any>(`${environment.api}/programs?$filter=year(startat) eq ${year} and programCode in(${programCode})`);
  }

  getTurnstile(value: string) {
    return this.http.get<any>(`${environment.api}/Turnstiles?$top=1&$filter=contains(description, '${value}')`);
  }

  getTurnstilesByUnitCode(unitCode: string): Observable<any> {
    return this.http.get<any>(`${environment.api}/turnstiles?filter=unitCode eq ${unitCode}`);
  }

  getProgramsCodes(): Observable<any> {
    return this.http.get<any>(`${environment.api}/turnstiles?$select=programsCodes`);
  }

  getUnitCode(): Observable<string> {
    return this.unitCode$.asObservable();
  }

  setUnitCode(unitCode: string) {
    this.unitCode$.next(unitCode);
  }

  getProgramCodes(): Observable<string> {
    return this.programCodes$.asObservable();
  }

  setProgramCodes(unitCode: string) {
    this.programCodes$.next(unitCode);
  }

  getTurnstileId(): Observable<number> {
    return this.turnstileId$.asObservable();
  }

  setTurnstileId(turnstileId: number) {
    this.turnstileId$.next(turnstileId);
  }
}
